import { useToast } from '@chakra-ui/react';

import { usePortalBusServicingCycleLazyQueryGQLType } from '~/backup/shared/views/deal-overview/dealOverview.gql.generated';

/**
 * Downloads one cycle's certificate PDF. The signed URL is not on any list row
 * (it is minted per request), so `download` lazily fetches the cycle detail and
 * opens the URL — or toasts when the PDF has not rendered yet. Call it once per
 * button: `loading` is that button's own request.
 */
export function useCertificateDownload(): {
  download: (servicingCycleId: string) => void;
  loading: boolean;
} {
  const toast = useToast();
  const [fetchCycle, { loading }] = usePortalBusServicingCycleLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const url = data.portalBusServicingCycle?.certificateDownloadUrl ?? null;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast({
          title: 'Certificate not ready',
          description: 'The certificate PDF has not finished rendering yet.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    onError: () => {
      toast({
        title: 'Could not fetch certificate',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  return {
    download: (servicingCycleId: string): void => {
      // Failures surface through onError's toast.
      fetchCycle({ variables: { servicingCycleId } }).catch(
        (): void => undefined,
      );
    },
    loading,
  };
}
